<template>
  <div>
    <load-profile />
    <!-- Table Container Card -->
    <b-card>
      <validation-observer
        ref="createForm"
        #default="{ invalid }"
      >
        <!-- form -->
        <n-form-confirmation
          key="createForm"
          :form="$refs.createForm"
          :disabled="invalid"
          confirm-button-text="button.create"
          @submit="submit"
        >
          <n-input
            v-model="data"
            :fields="fields"
          >
            <template #title="{ field }">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('label.title')"
                :rules="
                  locale === appLocale
                    ? 'required|max:100'
                    : 'max:100'
                "
                style="width: 100%;"
              >
                <b-form-group
                  :label="$t('label.title')"
                  :state="errors.length === 0 ? null : false"
                  class="mb-3"
                >
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1 mr-1">
                      <b-form-input
                        v-model="data[field.key][locale]"
                        :placeholder="$t('label.title')"
                        :state="errors.length === 0 ? null : false"
                      />
                    </div>
                    <div style="min-width: 80px">
                      <b-form-select
                        v-model="locale"
                        :options="locales"
                        class="language-select"
                      />
                    </div>
                  </div>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </template>
            <template #date="{ field }">
              <b-form-group
                :label-for="field.key"
                :class="field.class"
              >
                <label
                  :class="
                    field.hiddenKey &&
                      data[field.hiddenKey] === field.hiddenValue
                      ? 'd-none'
                      : 'd-block'
                  "
                >
                  <span>{{ $t(field.label) }}</span>
                </label>

                <!-- Regular datepicker when isRepeatEveryYear is false -->
                <template v-if="!data.isRepeatEveryYear">
                  <b-form-datepicker
                    :ref="field.key"
                    v-model="data[field.key]"
                    :locale="$i18n.locale"
                    :name="field.key"
                    :placeholder="$t(field.label)"
                    :date-format-options="{
                      year: 'numeric',
                      month: 'short',
                      day: '2-digit'
                    }"
                    v-bind="labels[$i18n.locale] || {}"
                    :min="field.min"
                  />
                </template>

                <!-- Custom MonthDayPicker when isRepeatEveryYear is true -->
                <template v-else>
                  <month-day-picker v-model="data[field.key]" />
                </template>
              </b-form-group>
            </template>
          </n-input>
          <b-row>
            <b-col
              cols="12"
              class="text-right"
            >
              <n-button-loading
                v-if="$can('create', permissionType)"
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="submit"
                class="mt-1 mr-1"
                variant="primary"
                :loading="loading"
                :disabled="invalid || data.date === ''"
              >
                {{ $t('button.create') }}
              </n-button-loading>
              <n-button-loading
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                :loading="loading"
                class="mt-1"
                @submit="back"
              >
                {{ $t('button.cancel') }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>
  </div>
</template>

<script>
import {
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BCol,
  BCard,
  BFormDatepicker,
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'
import Ripple from 'vue-ripple-directive'
import NButtonLoading from '@/components/NButtonLoading'
import NFormConfirmation from '@/components/NFormConfirmation'
import NInput from '@/components/NInput'
import MonthDayPicker from '@/components/NMonthDayPicker.vue'
import { mapValidationMessage } from '@/libs/helper'
import { locales } from 'moment-timezone'
import labels from '@/data/localeDatepicker'
import formInput from './formInput'

const HolidayRepository = Repository.get('holiday')

export default {
  components: {
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BCol,
    BCard,
    BFormDatepicker,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    MonthDayPicker,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      data: {
        title: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        date: '',
        description: '',
        isRepeatEveryYear: false,
      },
      loading: false,
      labels,
      locale: this.$i18n.locale.split('-')[0],
      locales: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ខ្មែរ' },
        { value: 'vi', text: 'Tiếng Anh' },
      ],
    }
  },
  computed: {
    appLocale() {
      return this.$i18n.locale.split('-')[0]
    }
  },
  watch: {
    '$i18n.locale'(val) {
      const [language] = val.split('-')
      this.locale = language
    },
    'data.isRepeatEveryYear': function (newVal, oldVal) {
      if (newVal !== oldVal && this.data.date) {
        const date = new Date(this.data.date)
        if (newVal) {
          // When switching to repeat every year, keep only month and day
          const currentYear = new Date().getFullYear()
          this.data.date = new Date(
            currentYear,
            date.getMonth(),
            date.getDate() + 1,
          )
            .toISOString()
            .split('T')[0]
        }
      }
    },
  },
  methods: {
    // Helper method to convert string title to object
    convertTitleToObject(title) {
      if (typeof title === 'string') {
        const defaultTitle = title
        const titleObj = {
          en: defaultTitle,
          zh: '',
          km: '',
          vi: '',
        }
        // Set the current locale's value
        const currentLocale = this.appLocale
        if (['en', 'zh', 'km', 'vi'].includes(currentLocale)) {
          titleObj[currentLocale] = defaultTitle
        }
        return titleObj
      }
      return title
    },
    submit() {
      this.$refs.createForm.validate().then(success => {
        if (success) {
          this.save()
        }
      })
    },
    save() {
      this.loading = true
      HolidayRepository.create(this.data)
        .then(response => {
          this.$router.push({
            name: `list-${this.route}`,
          })
        })
        .catch(error => {
          if (error.response?.status === 422) {
            const errorMessage = mapValidationMessage(
              error.response.data.validate,
            )
            this.$refs.createForm.setErrors(errorMessage)
          }
        })
        .then(() => {
          this.loading = false
        })
    },
    back() {
      this.$router.back()
    },
  },
  setup() {
    const fields = formInput
    const permissionType = 'manage-holiday'
    const route = 'holiday'
    return { fields, permissionType, route }
  },
}
</script>

<style lang="scss" scoped>
.language-select {
  height: 100%;
}
</style>
